import React, { useState, useEffect } from 'react';

/**
 * Celebration overlay for milestones and achievements.
 * Shows confetti + message, auto-dismisses after 4 seconds.
 */
const CELEBRATIONS = {
  first_meal: {
    emoji: null,
    title: 'First meal logged!',
    subtitle: "You're on your way. Consistency beats perfection.",
    color: '#6B9E8A',
  },
  streak_7: {
    emoji: null,
    title: '7-day streak!',
    subtitle: 'A full week of tracking. Your future self thanks you.',
    color: '#C67A5C',
  },
  streak_14: {
    emoji: null,
    title: '14-day streak!',
    subtitle: "Two weeks strong. You're building a real habit.",
    color: '#E5A84B',
  },
  streak_30: {
    emoji: null,
    title: '30-day streak!',
    subtitle: "A month of consistency. That's seriously impressive.",
    color: '#4A90D9',
  },
  streak_50: {
    emoji: null,
    title: '50-day streak!',
    subtitle: 'Half a hundred. This is who you are now.',
    color: '#9B59B6',
  },
  streak_100: {
    emoji: null,
    title: '100-day streak!',
    subtitle: 'Triple digits. You are unstoppable.',
    color: '#C67A5C',
  },
  first_recipe: {
    emoji: null,
    title: 'First recipe saved!',
    subtitle: 'Now you can log it in one tap. Smart move.',
    color: '#E5A84B',
  },
  full_day: {
    emoji: null,
    title: 'Full day logged!',
    subtitle: 'Breakfast, lunch, and dinner — complete picture.',
    color: '#6B9E8A',
  },
};

const CONFETTI_COLORS = ['#6B9E8A', '#C67A5C', '#4A90D9', '#E5A84B', '#D4736A', '#9B59B6'];

export const CelebrationOverlay = ({ type, onDismiss }) => {
  const [visible, setVisible] = useState(true);
  const celebration = CELEBRATIONS[type];

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(() => onDismiss?.(), 300);
    }, 4000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  if (!celebration) return null;

  return (
    <div
      data-testid={`celebration-${type}`}
      className={`fixed inset-0 z-[100] flex items-center justify-center transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`}
      onClick={() => { setVisible(false); setTimeout(() => onDismiss?.(), 300); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      {/* Confetti particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {Array.from({ length: 30 }).map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 rounded-full animate-confetti"
            style={{
              left: `${Math.random() * 100}%`,
              top: '-10px',
              backgroundColor: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
              animationDelay: `${Math.random() * 1.5}s`,
              animationDuration: `${2 + Math.random() * 2}s`,
            }}
          />
        ))}
      </div>

      {/* Card */}
      <div className="relative z-10 mx-6 max-w-sm w-full rounded-[24px] bg-white/95 dark:bg-[#141C22]/95 backdrop-blur-xl p-8 text-center shadow-2xl animate-fade-in-up">
        {/* Icon */}
        <div
          className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center"
          style={{ backgroundColor: celebration.color + '15' }}
        >
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 16.8l-6.2 4.5 2.4-7.4L2 9.4h7.6L12 2z"
              fill={celebration.color}
            />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-[#1A3A4A] dark:text-[#F0EDE8] mb-2">{celebration.title}</h2>
        <p className="text-sm text-[#7A7A72] dark:text-[#A1A1AA] leading-relaxed">{celebration.subtitle}</p>
        <button
          className="mt-5 px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition hover:opacity-90"
          style={{ backgroundColor: celebration.color }}
        >
          Keep going
        </button>
      </div>
    </div>
  );
};

export default CelebrationOverlay;
